import { getUserByMultiRoles } from '../../store/slices/userSlice';
import { extractList } from '../../utils/response';

/** Manager role (logged-in user) → assignable user roles for lead assignment */
export const LEAD_ASSIGN_MANAGER_ROLE_MAP = {
  'qc manager': ['QC'],
  'ops manager': ['Ops'],
  'qa manager': ['QA', 'IUW', 'QA / IUW'],
  'csr manager': ['CSR (Support)'],
  'senior account manager': ['Account Manager'],
};

export function getCurrentUserRoleName(user) {
  return (user?.roles?.[0]?.name || user?.role?.name || '').toLowerCase().trim();
}

export function getAssignableRolesForManager(user) {
  const roleName = getCurrentUserRoleName(user);
  return LEAD_ASSIGN_MANAGER_ROLE_MAP[roleName] ?? null;
}

/** Role-filtered managers assign fresh — do not prefill existing lead assignees */
export function shouldPrefillLeadAssignees(user) {
  return !getAssignableRolesForManager(user);
}

export function isLeadAssignManagerRole(user) {
  return Boolean(getAssignableRolesForManager(user));
}

export function getUserIdentityIds(user) {
  return new Set([user?.id, user?.uuid].filter(Boolean).map(String));
}

function toBoolFlag(value) {
  if (typeof value === 'string') {
    return ['1', 'true', 'yes'].includes(value.toLowerCase().trim());
  }
  return value === true || value === 1;
}

/**
 * Lead API `is_assigned` — top-level or on last_status_log.lead_assignees[].
 */
export function isLeadAssignedFlag(lead) {
  if (!lead) return false;
  if (toBoolFlag(lead.is_assigned)) return true;

  const fromStatusLog = lead?.last_status_log?.lead_assignees;
  if (Array.isArray(fromStatusLog) && fromStatusLog.some((row) => toBoolFlag(row?.is_assigned))) {
    return true;
  }

  const assignees = lead?.assignees;
  if (Array.isArray(assignees) && assignees.some((row) => toBoolFlag(row?.is_assigned))) {
    return true;
  }

  return false;
}

/**
 * Manager view: API says assigned → show "Assign" status and lock actions.
 */
export function shouldShowAssignStatusForManager(lead, user) {
  return isLeadAssignManagerRole(user) && isLeadAssignedFlag(lead);
}

export function formatUserRoles(user) {
  const raw = user?.roles;
  if (Array.isArray(raw) && raw.length) {
    return raw.map((r) => (typeof r === 'string' ? r : r?.name)).filter(Boolean).join(', ');
  }
  if (user?.role?.name) return user.role.name;
  if (typeof user?.role === 'string') return user.role;
  return '';
}

export function normalizeAssignUserOption(user) {
  return {
    id: user?.uuid ?? user?.id,
    name: user?.name || user?.username || 'Unnamed',
    email: user?.email ?? '',
    roles: user?.roles,
    role: user?.role,
  };
}

export function normalizeAssignUserOptions(list = []) {
  return (Array.isArray(list) ? list : [])
    .map(normalizeAssignUserOption)
    .filter((user) => user.id);
}

export async function fetchLeadAssignUserOptions(dispatch, user, fallbackUsersList = []) {
  const assignableRoles = getAssignableRolesForManager(user);

  if (!assignableRoles?.length) {
    return normalizeAssignUserOptions(fallbackUsersList);
  }

  const res = await dispatch(
    getUserByMultiRoles({ 'roles[]': assignableRoles, paginate: 0 })
  ).unwrap();

  const list = extractList(res?.data ?? res);
  return normalizeAssignUserOptions(list);
}

export function getUserDisplayLabel(user) {
  const name = user?.name ?? '';
  const roles = formatUserRoles(user);
  return roles ? `${name} (${roles})` : name;
}
